import { getApiBaseUrl, getAdminAuthToken } from './auth_service';

/**
 * TodoService - Manages todo operations via the backend API.
 * Data is persisted in the database and scoped to the authenticated user.
 */
class TodoService {
	constructor(userId) {
		if (!userId) {
			throw new Error('TodoService requires a userId');
		}
		this.userId = userId;
	}

	async _getHeaders() {
		const token = await getAdminAuthToken();
		return {
			Authorization: `Bearer ${token}`,
			'Content-Type': 'application/json',
		};
	}

	/**
	 * Get all todos for the current user
	 * @returns {Promise<Array>} Array of todo objects
	 */
	async getTodos() {
		const headers = await this._getHeaders();
		const res = await fetch(`${getApiBaseUrl()}/api/todos`, { headers });
		if (!res.ok) {
			throw new Error('Failed to fetch todos');
		}
		return res.json();
	}

	/**
	 * Create a new todo
	 * @param {Object} todoData - Todo data { title, description, dueDate, priority }
	 * @returns {Promise<Object>} The created todo object
	 */
	async createTodo(todoData) {
		const headers = await this._getHeaders();
		const res = await fetch(`${getApiBaseUrl()}/api/todos`, {
			method: 'POST',
			headers,
			body: JSON.stringify({
				title: todoData.title || '',
				description: todoData.description || '',
				dueDate: todoData.dueDate || null,
				priority: todoData.priority || 'medium',
			}),
		});
		if (!res.ok) {
			throw new Error('Failed to create todo');
		}
		return res.json();
	}

	/**
	 * Update an existing todo
	 * @param {string} id - Todo ID
	 * @param {Object} updates - Fields to update
	 * @returns {Promise<Object|null>} Updated todo or null if not found
	 */
	async updateTodo(id, updates) {
		const headers = await this._getHeaders();
		const res = await fetch(`${getApiBaseUrl()}/api/todos/${id}`, {
			method: 'PUT',
			headers,
			body: JSON.stringify({
				title: updates.title || '',
				description: updates.description || '',
				dueDate: updates.dueDate || null,
				priority: updates.priority || 'medium',
				completed: updates.completed ?? false,
			}),
		});
		if (!res.ok) return null;
		return res.json();
	}

	/**
	 * Delete a todo
	 * @param {string} id - Todo ID
	 * @returns {Promise<boolean>} True if deleted, false if not found
	 */
	async deleteTodo(id) {
		const headers = await this._getHeaders();
		const res = await fetch(`${getApiBaseUrl()}/api/todos/${id}`, {
			method: 'DELETE',
			headers,
		});
		return res.ok;
	}

	/**
	 * Toggle todo completion status
	 * @param {string} id - Todo ID
	 * @returns {Promise<Object|null>} Updated todo or null if not found
	 */
	async toggleTodo(id) {
		const headers = await this._getHeaders();
		const res = await fetch(`${getApiBaseUrl()}/api/todos/${id}/toggle`, {
			method: 'PATCH',
			headers,
		});
		if (!res.ok) return null;
		return res.json();
	}
}

export default TodoService;
